/**
 * Frozen Stock Review List
 *
 * Lists frozen stock allocations (freeze requests) with warehouse approval actions.
 * - pending: freeze request awaiting warehouse approval (stock NOT locked yet)
 * - approved: freeze in effect (stock locked)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Lock, CheckCircle, XCircle } from 'lucide-react';
import warehouseService from '../../services/warehouseService';

const FrozenStockList = ({ refreshTrigger, onRefresh, canApprove = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total_count: 0, total_pages: 0 });

  const loadFrozenStock = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
      };
      if (statusFilter !== 'all') {
        params.freeze_status = statusFilter;
      }
      const response = await warehouseService.getFrozenStock(params);
      setItems(response.items || []);
      setPagination(prev => ({
        ...prev,
        total_count: response.total_count || 0,
        total_pages: response.total_pages || 0,
      }));
    } catch (err) {
      setError(err.response?.data?.error || '加载冻结库存失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, statusFilter]);

  useEffect(() => {
    loadFrozenStock();
  }, [loadFrozenStock, refreshTrigger]);

  const handleApprove = async (item) => {
    if (!window.confirm(`确定批准冻结申请 ${item.movement_id} 吗？批准后库存将被锁定。`)) return;
    try {
      setActionLoading(item._id);
      await warehouseService.approveFrozenStock(item._id);
      await loadFrozenStock();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || '批准失败');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (item) => {
    const isPending = item.freeze_status === 'pending';
    const confirmText = isPending
      ? `确定驳回冻结申请 ${item.movement_id} 吗？`
      : `确定解冻 ${item.movement_id} 吗？解冻后库存将恢复可用。`;
    if (!window.confirm(confirmText)) return;
    try {
      setActionLoading(item._id);
      await warehouseService.rejectFrozenStock(item._id);
      await loadFrozenStock();
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || (isPending ? '驳回失败' : '解冻失败'));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div>
      {/* Filter */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">
          共 {pagination.total_count} 条冻结记录
        </p>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPagination(prev => ({ ...prev, page: 1 })); }}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue placeholder="状态" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部状态</SelectItem>
            <SelectItem value="pending">待审核</SelectItem>
            <SelectItem value="approved">已批准</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <p className="text-sm text-destructive mb-4">{error}</p>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Lock className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>暂无冻结库存记录</p>
        </div>
      ) : (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">冻结单号</th>
                <th className="px-3 py-2 text-left font-semibold">销售单</th>
                <th className="px-3 py-2 text-left font-semibold">产品</th>
                <th className="px-3 py-2 text-left font-semibold">库位/批次</th>
                <th className="px-3 py-2 text-right font-semibold">数量</th>
                <th className="px-3 py-2 text-left font-semibold">客户</th>
                <th className="px-3 py-2 text-left font-semibold">申请人</th>
                <th className="px-3 py-2 text-left font-semibold">状态</th>
                {canApprove && <th className="px-3 py-2 text-center font-semibold">操作</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map(item => {
                const product = item.products?.[0] || {};
                const isPending = item.freeze_status === 'pending';
                return (
                  <tr key={item._id} className="hover:bg-muted/50">
                    <td className="px-3 py-2 font-mono text-xs">{item.movement_id}</td>
                    <td className="px-3 py-2">{item.source_reference || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{product.name || product.product_code || '-'}</div>
                      {product.name && product.product_code && (
                        <div className="text-xs text-muted-foreground">{product.product_code}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {product.location || item.warehouse_location || 'main'}
                      {product.batch_number && (
                        <span className="text-xs text-muted-foreground ml-1">/ {product.batch_number}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-medium">{item.total_quantity ?? product.quantity ?? 0}</td>
                    <td className="px-3 py-2">{item.client_name || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="text-xs">{item.freeze_requested_by_name || item.freeze_requested_by || item.created_by || '-'}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(item.freeze_requested_at || item.created_at)}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {isPending ? (
                        <Badge variant="outline" className="border-warning text-warning">待审核</Badge>
                      ) : (
                        <Badge variant="outline" className="border-success text-success">已冻结</Badge>
                      )}
                    </td>
                    {canApprove && (
                      <td className="px-3 py-2">
                        <div className="flex justify-center gap-2">
                          {isPending && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-success border-success h-7"
                              disabled={actionLoading === item._id}
                              onClick={() => handleApprove(item)}
                            >
                              <CheckCircle className="h-3.5 w-3.5 mr-1" />
                              批准
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive h-7"
                            disabled={actionLoading === item._id}
                            onClick={() => handleReject(item)}
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1" />
                            {isPending ? '驳回' : '解冻'}
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page <= 1}
          >
            上一页
          </Button>
          <span className="text-sm text-muted-foreground px-4">
            第 {pagination.page} / {pagination.total_pages} 页
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page >= pagination.total_pages}
          >
            下一页
          </Button>
        </div>
      )}
    </div>
  );
};

export default FrozenStockList;
